<template>
  <div class='cloud-accounts-tab'>
    <div class="cloud-accounts-tab__headers">
      <div class='cloud-accounts-tab__headers__search'>
        <filtered-search
          @searchInput="searchChange"
          :show-filters="false"
          :searchItem='search'
        />
      </div>
    </div>
    <configuration-item-header
      tab="accounts"
      :test-options="[]"
      :url-count="urlCount"
      :selected-items="selectedItems"
      :excluded-items="excludedItems"
      :allSelected="isAllSelected"
      :all-items-count="cloudAccountCount"
      :selectAllLabel="$t('configuration.cloud.accounts.selected').toString()"
      @select-all="selectAll"
      @clearSelected="resetSelectedItems"
      @apply='apply'
    >
      <template slot="additional-buttons">
        <v-btn class="add-cloud-asset-btn" @click="showAddAccountPopup = true">
          {{ $t('configuration.add.account') }}
        </v-btn>
      </template>
    </configuration-item-header>
    <div v-if="!dataLoading && !cloudAccountsLoading">
      <div v-if='cloudAccountsItems.length'>
        <assets-column-headers
          :display='true'
          :headers='headersForTab'
          tab='cloudAccount'
        />
        <cloud-account-row
          v-for="(cloudAccount, index) in cloudAccountsItems"
          :item="cloudAccount"
          tab="account"
          :key="index"
          :selected-items='selectedItems'
          :amountOfTestCardInRow='amountOfTestCardInRow'
          :is-selected="isSelected(cloudAccount)"
          :add-test-card-disabled='isAddTestDisabled(cloudAccount)'
          @checked='checked'
          @openAddTestPopup='openAddTestCardPopup(cloudAccount)'
          @openTagsPopup='openTagsPopup'
          @submitTagsInformation='submitTagsInformation'
        />
        <div class="cloud-assets-tab__pagination">
          <advanced-pagination
            tab='cloudAccount'
            :is-show-pagination='isShowPagination'
            :pages-amount='pages'
            :page="page"
            @changedSelectedPage='changedSelectedPage'
            @changedItemsAmountPerPage='changedItemsAmountPerPage'
          />
        </div>
      </div>
      <no-data
        v-else
        :message='$t("cloud.accounts.no.data.message").toString()'
      >
        <div slot="action" v-if='IsTenantAdmin'>
          <v-btn class="configure-organizations-btn" @click="goToOrganizations">
            {{ $t('configure.organization.button.text') }}
          </v-btn>
        </div>
      </no-data>
    </div>
    <div class='cloud-accounts-tab-loading' v-else />
    <page-loading-overlay :show="cloudAccountsLoading && currentTab === 'cloud_accounts'" contained />
    <add-test-popup
      v-model="addTestCardPopup"
      :website="cloudAccountForTestCard"
      :tld="cloudAccountForTestCard.tld"
      :tab="'cloudAccount'"
      :organization='cloudAccountForTestCard.organization'
      :testTypes="currentTestTypesForNewTest"
      :enableCloudComplianceFeature='enableCloudComplianceFeature'
      @submit="addTestCard"
    />
    <add-cloud-account-popup
      v-model="showAddAccountPopup"
      @addCloudAccounts='addCloudAccounts'
      :loading='loading'
    />
  </div>
</template>

<script>
import FilteredSearch from '@/components/common/FilteredSearch.vue';
import { mapGetters } from 'vuex';
import AdvancedPagination from '@/components/common/AdvancedPagination.vue';
import AssetsColumnHeaders from '@/components/configuration/AssetsColumnHeaders.vue';
import CloudAccountRow from '@/components/configuration/CloudAccountRow.vue';
import { UPDATE_ITEMS_PER_PAGE } from '@/store/pagination/actionTypes';
import {
  ADD_NEW_TEST_PROJECT, ADD_URL_CONFIGURATIONS,
  CHANGE_CLOUD_ACCOUNTS_LIST_OPTIONS,
  INITIATE_TEST_CARD,
  LOAD_TEST_CARDS_CLOUD_ACCOUNTS, UPDATE_SELECTED_CLOUD_ACCOUNTS,
} from '@/store/project/actionTypes';
import PageLoadingOverlay from '@/components/common/PageLoadingOverlay.vue';
import AddTestPopup from '@/components/popup/AddTestPopup.vue';
import { TEST_TYPES_ACCOUNTS } from '@/constants/projects';
import { CONFIGURATION, CONFIGURATION_ACTION_TYPES } from '@/constants/configuration';
import ConfigurationItemHeader from '@/components/configuration/ConfigurationItemHeader.vue';
import AddCloudAccountPopup from '@/components/popup/AddCloudAccountPopup.vue';
import NoData from '@/components/common/NoData.vue';
import store from '@/store';
import { views } from '@/router/constants';

export default {
  name: 'CloudAccountsTab',
  components: {
    NoData,
    AddCloudAccountPopup,
    ConfigurationItemHeader,
    AddTestPopup,
    PageLoadingOverlay,
    CloudAccountRow,
    AssetsColumnHeaders,
    AdvancedPagination,
    FilteredSearch,
  },
  props: {
    accountsSelected: {},
    amountOfTestCardInRow: {
      type: Number,
      default: 5,
      required: false,
    },
    enableCloudComplianceFeature: {
      type: Boolean,
      default: false,
      required: false,
    },
    currentTab: {
      required: false,
    },
  },
  async created() {
    if (this.currentTab === 'cloud_accounts' && this.organizationFilter.length) {
      await this.$store.dispatch(CHANGE_CLOUD_ACCOUNTS_LIST_OPTIONS, {
        page: this.page,
        search: this.search,
        organization: this.organizationFilter,
        turnOnLoading: true,
      });
      await this.$store.dispatch(LOAD_TEST_CARDS_CLOUD_ACCOUNTS);
    }
  },
  data() {
    return {
      headersForTab: [
        'cloud.account.tab.id.header',
        'cloud.account.tab.type.header',
      ],
      page: 1,
      search: '',
      filterSearch: [],
      showAddAccountPopup: false,
      addTestCardPopup: false,
      currentTestTypesForNewTest: [...Object.values(TEST_TYPES_ACCOUNTS)],
      cloudAccountForTestCard: {},
      loading: false,
      selectedItems: [],
      excludedItems: [],
      isAllSelected: false,
      filtersForPayload: {
        search: '',
        filter: [],
      },
      firstTimeCalled: false,
    };
  },
  computed: {
    ...mapGetters([
      'configurationCloudAccounts',
      'dataLoading',
      'cloudAccountsLoading',
      'licenses',
      'amountOfRunningProjects',
      'cloudAccountsPaginationState',
      'testCardsCloudAccounts',
      'organizationFilter',
      'domainFilter',
    ]),
    urlCount() {
      if (this.isAllSelected) {
        return (this.filtersForPayload.total - this.excludedItems.length + this.selectedItems.length);
      }
      return this.selectedItems.length || 0;
    },
    cloudAccountCount() {
      return parseInt(this.cloudAccountsPaginationState.total, 10);
    },
    IsTenantAdmin: () => store.getters.permissions?.includes('all.tenant.users') || false,
    listOfCloudAccounts() {
      return this.configurationCloudAccounts
        .filter(item => this.organizationFilter.includes(item.organization.name));
    },
    cloudAccountsItems() {
      return this.listOfCloudAccounts
        .filter(item => (!this.search.length || (this.search.length > 0 && item.configData.includes(this.search))))
        .map(item => ({
          ...item,
          organization: item.company,
          type: item.type.toLowerCase(),
          url: item.configData,
          testCards: this.testCardsCloudAccounts?.[item.configData] || [],
        }));
    },
    isIndeterminate() {
      return this.selectedItems.length !== this.cloudAccountsItems.length;
    },
    selectAllLabel() {
      return this.$t('configuration.infrastructure.assets.selected.assets');
    },
    pages() {
      return this.cloudAccountsPaginationState?.pageCount || Math.ceil(this.cloudAccountsItems.length / this.itemsPerPage) || 1;
    },
    isShowPagination() {
      return this.pages > 1;
    },
  },
  methods: {
    isAddTestDisabled(item) {
      return !this.licenses[item.organization.name]?.length && this.amountOfRunningProjects[item.organization.name] > 2;
    },
    async addCloudAccounts(payload) {
      this.loading = true;
      const configData = payload.items.map(item => ({
        settingsMetaData: {
          provider: item.provider.toLowerCase(),
        },
        data: item.url,
        metaData: [],
        tld: item.tld,
        subdomain: null,
        companyId: item.companyId,
      }));
      await this.$store.dispatch(ADD_URL_CONFIGURATIONS, {
        payload: configData,
        configType: payload.configType,
        successMessageType: CONFIGURATION_ACTION_TYPES.ADD_ASSET,
      });
      await this.$store.dispatch(CHANGE_CLOUD_ACCOUNTS_LIST_OPTIONS, {
        page: this.page,
        search: this.search,
        organization: this.organizationFilter,
        turnOnLoading: true,
      });
      this.loading = false;
    },
    async addTestCard(test) {
      const testWithTld = test;
      testWithTld.tld = this.cloudAccountForTestCard.tld;
      if (test.type === TEST_TYPES_ACCOUNTS.cloud_compliance && !this.enableCloudComplianceFeature) {
        return;
      }
      testWithTld.metaData = {
        ...testWithTld.metaData,
        assetType: CONFIGURATION.CONFIG_TYPES.CLOUD_ACCOUNT,
      };
      try {
        const createdTest = await this.$store.dispatch(ADD_NEW_TEST_PROJECT, testWithTld);
        if (!testWithTld.schedule && createdTest) {
          await this.$store.dispatch(INITIATE_TEST_CARD, [createdTest.id]);
        }
      } catch (e) {
        this.errorText = e.message;
      }
      await this.$store.dispatch(LOAD_TEST_CARDS_CLOUD_ACCOUNTS);
    },
    async submitTagsInformation(payload) {
      await this.$emit('submitTagsInformation', { items: payload.items, configType: payload.configType, action: payload.action });
    },
    openAddTestCardPopup(cloudItem) {
      if (!this.isAddTestDisabled(cloudItem)) {
        this.addTestCardPopup = true;
        this.cloudAccountForTestCard = { configData: cloudItem.url, ...cloudItem };
      }
    },
    openTagsPopup(assets) {
      this.$emit('toggleTagsPopup', assets);
    },
    checked(item) {
      if (this.isAllSelected) {
        if (item?.selected !== undefined) {
          if (item.selected) {
            const index = this.excludedItems.findIndex(data => data === item.url);
            if (index !== -1) {
              this.excludedItems.splice(index, 1);
            } else {
              this.excludedItems.push(item.url);
            }
          } else {
            const index = this.selectedItems.findIndex(data => data === item.url);
            if (index !== -1) {
              this.selectedItems.splice(index, 1);
            } else {
              this.selectedItems.push(item.url);
            }
            if (this.excludedItems.includes(item.url)) {
              const excludeIndex = this.excludedItems.findIndex(data => data === item.url);
              this.excludedItems.splice(excludeIndex, 1);
              this.filtersForPayload.total -= 1;
            }
          }
        } else {
          const index = this.excludedItems.findIndex(data => data === item.url);
          if (index !== -1) {
            this.excludedItems.splice(index, 1);
          } else {
            this.excludedItems.push(item.url);
          }
        }
      } else {
        const index = this.selectedItems.findIndex(data => data === item.url);
        if (index !== -1) {
          this.selectedItems.splice(index, 1);
        } else {
          this.selectedItems.push(item.url);
        }
      }
    },
    isSelected(asset) {
      return this.selectedItems.includes(asset.url) ||
        (this.isAllSelected && asset?.selected !== undefined && asset.selected && !this.excludedItems.includes(asset.url)) ||
        (this.isAllSelected && asset?.selected === undefined && !this.excludedItems.includes(asset.url));
    },
    selectAll() {
      this.isAllSelected = !this.isAllSelected;
      if (this.selectedItems.length) {
        this.selectedItems = [];
      }
      if (this.excludedItems.length) {
        this.excludedItems = [];
      }
      this.resetFiltersForPayload();
    },
    async apply() {
      await this.$emit('apply');
    },
    searchChange(e) {
      this.search = e.trim();
      this.selectedItems = [];
    },
    async changedSelectedPage(currentPage) {
      window.scrollTo(0, 0);
      this.page = currentPage;
      await this.$store.dispatch(CHANGE_CLOUD_ACCOUNTS_LIST_OPTIONS, {
        page: this.page,
        search: this.search,
        organization: this.organizationFilter,
        turnOnLoading: true,
      });
    },
    async goToOrganizations() {
      await this.$router.push({ name: views.SETTINGS, hash: '#organizations-settings' });
    },
    async changedItemsAmountPerPage(itemsPerPage) {
      window.scrollTo(0, 0);
      this.page = 1;
      await this.$store.dispatch(UPDATE_ITEMS_PER_PAGE, { itemsPerPage, tab: 'cloudAccount' });
      await this.$store.dispatch(CHANGE_CLOUD_ACCOUNTS_LIST_OPTIONS, {
        search: this.search,
        limit: itemsPerPage,
        page: this.page,
        organization: this.organizationFilter,
        turnOnLoading: true,
      });
    },
    resetFiltersForPayload() {
      this.filtersForPayload = {
        search: this.search,
        filter: this.filterSearch,
        total: this.cloudAccountsPaginationState.total,
      };
    },
    resetSelectedItems() {
      this.selectedItems = [];
      this.excludedItems = [];
      this.isAllSelected = false;
      this.filtersForPayload = {};
    },
  },
  watch: {
    async domainFilter() {
      if (this.currentTab !== 'cloud_accounts' && this.domainFilter.length) {
        this.firstTimeCalled = false;
      }
      if (this.currentTab === 'cloud_accounts' && this.organizationFilter.length) {
        this.page = 1;
        await this.$store.dispatch(CHANGE_CLOUD_ACCOUNTS_LIST_OPTIONS, {
          page: this.page,
          search: this.search,
          organization: this.organizationFilter,
          turnOnLoading: true,
        });
        await this.$store.dispatch(LOAD_TEST_CARDS_CLOUD_ACCOUNTS);
      }
      this.resetSelectedItems();
    },
    excludedItems() {
      if (this.excludedItems.length === this.filtersForPayload.total && this.isAllSelected) {
        this.isAllSelected = false;
      }
    },
    selectedItems() {
      if (this.selectedItems.length === this.filtersForPayload.total && !this.isAllSelected) {
        this.isAllSelected = true;
      }
    },
    async isAllSelected() {
      if (!this.isAllSelected) {
        this.excludedItems = [];
        this.resetFiltersForPayload();
      }
      if (this.isAllSelected) {
        const items = [];
        this.configurationCloudAccounts.forEach(item => {
          items.push({ ...item, selected: true });
        });
        await this.$store.dispatch(UPDATE_SELECTED_CLOUD_ACCOUNTS, items);
        this.selectedItems = [];
        this.resetFiltersForPayload();
      }
    },
    async organizationFilter() {
      if (this.currentTab === 'cloud_accounts' && this.organizationFilter.length) {
        this.page = 1;
        await this.$store.dispatch(CHANGE_CLOUD_ACCOUNTS_LIST_OPTIONS, {
          page: this.page,
          search: this.search,
          organization: this.organizationFilter,
          turnOnLoading: true,
        });
      }
    },
    async currentTab() {
      if (this.currentTab === 'cloud_accounts' && !this.firstTimeCalled && this.organizationFilter.length) {
        this.firstTimeCalled = true;
        this.page = 1;
        await this.$store.dispatch(CHANGE_CLOUD_ACCOUNTS_LIST_OPTIONS, {
          page: this.page,
          search: this.search,
          organization: this.organizationFilter,
          turnOnLoading: true,
        });
        await this.$store.dispatch(LOAD_TEST_CARDS_CLOUD_ACCOUNTS);
      }
      this.resetSelectedItems();
    },
  },
};
</script>

<style lang='scss' scoped>
@import '~@/assets/styles/design-system';

.configure-organizations-btn {
  width: fit-content;
  padding: $space-1 $space-1;
  background-color: var(--brand-primary);
  border-radius: $radius-sm;
  box-shadow: $shadow-sm;
  color: var(--text-inverse);
  font-family: $default-application-font-medium;
  font-size: $font-size-sm;
  font-weight: 500;
  letter-spacing: 1.1px;
  line-height: $space-4;
  text-align: center;
  margin-bottom: $space-2;
}

.cloud-accounts-tab-loading {
  align-self: center;
  justify-self: center;
  margin-top: 20%;
}

.cloud-accounts-tab {
  display: flex;
  flex-direction: column;
  padding: $space-4 $space-8;
  position: relative; // Required for contained loading overlay
  min-height: calc(100vh - 200px); // Ensure overlay has space to display

  .add-cloud-asset-btn {
    background-color: var(--text-secondary) !important;
    border-radius: $radius-sm;
    box-shadow: $shadow-sm;
    color: #ffffff !important;
    font-family: $default-application-font-medium;
    font-size: $font-size-sm;
    height: $space-8;
    letter-spacing: 1.1px;
    line-height: $space-4;
    width: rem(143);
    margin-right: $space-5;
    text-transform: uppercase;
  }

  &__pagination {
    padding-top: $space-5;
  }

  .no-results {
    text-align: center;
    color: var(--text-muted);
    font-family: $default-application-font-regular;
    font-size: $font-size-4xl;
    line-height: rem(40);
    margin-top: $space-8;
  }

  &__headers {
    display: flex;
    margin-top: -$space-5;
    margin-left: -$space-0-5;

    &__search {
      width: rem(700);
      margin-bottom: $space-4;
    }

    &__bottom {
      margin-bottom: $space-5;
      margin-top: $space-5;
      display: flex;
      align-items: center;

      .select-all {
        display: flex;
        margin-right: $space-8;
      }
    }
  }
}
</style>
